import apiService from "./api";

// Interfaces based on MaintenanceRequest model
export interface MaintenanceRequest {
  _id: string;
  requestId: string;
  companyId: any;
  propertyId: any;
  rentalUnitId: any;

  // Requester Information
  requesterId: any;
  requesterType: "tenant" | "admin" | "manager" | "owner";
  requesterName: string;
  requesterEmail: string;
  requesterPhone?: string;

  // Request Details
  type: string;
  maintenanceTypeId?: any;
  title: string;
  description: string;
  priority: "low" | "medium" | "high" | "urgent";
  status:
    | "pending"
    | "assigned"
    | "accepted"
    | "in-progress"
    | "on-hold"
    | "completed"
    | "closed"
    | "cancelled";

  // Location Details
  location?: {
    room?: string;
    specificLocation?: string;
    floor?: string;
    additionalInfo?: string;
  };
  accessInstructions?: string;

  // Assignment Information
  assignedTo?: {
    userId: any;
    name: string;
    email: string;
    phone?: string;
    assignedAt: Date;
    assignedBy?: any;
    acceptedAt?: Date;
    acceptanceToken?: string;
    acceptanceExpiresAt?: Date;
  };

  // Assignment History (for broadcast method)
  assignmentOffers?: Array<{
    userId: any;
    name: string;
    email: string;
    offerToken?: string;
    offerSentAt: Date;
    offerExpiresAt?: Date;
    status: "pending" | "accepted" | "expired" | "rejected";
    respondedAt?: Date;
  }>;

  // Time Tracking
  estimatedResolutionTime?: Date;
  actualResolutionTime?: Date;
  responseTime?: Date;
  slaDeadline?: Date;

  // Scheduling
  preferredTimeSlots?: Array<{
    date: Date;
    startTime: string;
    endTime: string;
    isAvailable: boolean;
  }>;
  scheduledDate?: Date;
  scheduledTime?: {
    start: string;
    end: string;
  };

  // Media
  media?: Array<{
    uploadedBy?: any;
    uploadedByName?: string;
    uploadedByType?: "requester" | "assignee" | "admin" | "watcher";
    userType?: "TENANT" | "ASSIGNEE" | "ADMIN";
    url: string;
    publicId?: string;
    type?: "image" | "video" | "document";
    description?: string;
    category?: "before" | "during" | "after" | "issue" | "document";
    uploadedAt?: Date;
  }>;

  // Comments
  internalNotes?: Array<{
    authorId: any;
    authorName: string;
    authorRole: string;
    note: string;
    isEdited?: boolean;
    editedAt?: Date;
    createdAt: Date;
  }>;

  publicComments?: Array<{
    authorId: any;
    authorName: string;
    authorType: "requester" | "assignee" | "admin" | "watcher";
    comment: string;
    isEdited?: boolean;
    editedAt?: Date;
    createdAt: Date;
  }>;

  // Status History
  statusHistory?: Array<{
    status: string;
    changedBy?: any;
    changedByName?: string;
    changedAt: Date;
    reason?: string;
    notes?: string;
  }>;

  // Tracking
  watchers?: any[];
  publicTrackingId?: string;
  assigneeTrackingId?: string;

  // Cost Tracking
  actualCost?: {
    labor?: { amount?: number | string; description?: string; pseudoAmount?: number };
    materials?: { amount?: number | string; description?: string; pseudoAmount?: number };
    other?: { amount?: number | string; description?: string; pseudoAmount?: number };
    total?: number;
    pseudoTotal?: number;
    currency?: string;
  };
  expenses?: MaintenanceExpenseMap;

  // Work Details
  workDetails?: {
    workPerformed?: string;
    materialsUsed?: Array<{
      name: string;
      quantity: number;
      unit?: string;
      cost: number;
    }>;
    timeSpent?: {
      hours?: number;
      minutes?: number;
    };
    techniciansInvolved?: number;
    notes?: string;
  };

  // Feedback
  feedback?: {
    rating?: number;
    ratingCategories?: {
      quality?: number;
      timeliness?: number;
      communication?: number;
      professionalism?: number;
    };
    comments?: string;
    submittedAt?: Date;
    submittedBy?: any;
    wouldRecommend?: boolean;
  };

  // Metadata
  tags?: string[];
  customFields?: any;
  source?: "web" | "mobile" | "api" | "phone" | "email" | "chat";

  // Timestamps
  createdAt: string;
  updatedAt: string;
  completedAt?: Date;
  closedAt?: Date;
  cancelledAt?: Date;
  cancelledBy?: any;
  cancellationReason?: string;

  // Virtuals (from backend)
  isOverdue?: boolean;
  timeToComplete?: number;
  costVariance?: number;
  responseTimeMinutes?: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface MaterialExpenseLineInput {
  source: "inventory" | "temporary";
  inventoryItemId?: string;
  title: string;
  quantity: number;
  unitPrice?: number | string;
}

export interface MaterialExpensePayload {
  items: MaterialExpenseLineInput[];
  assignMaterialExpenseToAssignee: boolean;
}

export interface MaintenanceExpenseDocument {
  _id: string;
  subtype: "labour" | "material" | "other";
  amount: number;
  description: string;
  status: string;
  metadata?: any;
}

export interface MaintenanceExpenseMap {
  labour: MaintenanceExpenseDocument | null;
  material: MaintenanceExpenseDocument | null;
  other: MaintenanceExpenseDocument | null;
}

export interface MaterialSearchItem {
  _id: string;
  title: string;
  titleEnglish?: string;
  price: number;
  quantity: number;
  categoryTitle?: string;
  categoryTitleEnglish?: string;
}

export const createMaintenanceRequest = async (requestData: {
  companyId: string;
  propertyId: string;
  rentalUnitId: string;
  type: string;
  title: string;
  description: string;
  priority?: "low" | "medium" | "high" | "urgent";
  location?: {
    room?: string;
    specificLocation?: string;
    floor?: string;
    additionalInfo?: string;
  };
  preferredTimeSlots?: Array<{
    date: string;
    startTime: string;
    endTime: string;
  }>;
  media?: string[];
  requesterEmail?: string;
  requesterPhone?: string;
}): Promise<ApiResponse<MaintenanceRequest>> => {
  try {
    const response = await apiService.post(
      "/maintenance/requests",
      requestData
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to create maintenance request" }
    );
  }
};

export const getMaintenanceRequests = async (
  params: {
    page?: number;
    limit?: number;
    status?: string;
    priority?: string;
    type?: string;
    propertyId?: string;
    rentalUnitId?: string;
    requesterId?: string;
    assignedTo?: string;
    companyId?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  } = {}
): Promise<{
  success: boolean;
  data: MaintenanceRequest[];
  totalCount: number;
  count: number;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}> => {
  try {
    const response = await apiService.get("/maintenance/requests", { params });
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to fetch maintenance requests" }
    );
  }
};

export const getMaintenanceRequestById = async (
  requestId: string
): Promise<ApiResponse<MaintenanceRequest>> => {
  try {
    const response = await apiService.get(`/maintenance/requests/${requestId}`);
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to fetch maintenance request" }
    );
  }
};

export const updateMaintenanceRequest = async (
  requestId: string,
  updates: Partial<MaintenanceRequest>
): Promise<ApiResponse<MaintenanceRequest>> => {
  try {
    const response = await apiService.put(
      `/maintenance/requests/${requestId}`,
      updates
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to update maintenance request" }
    );
  }
};

export const assignMaintenanceRequest = async (
  requestId: string,
  userId: string
): Promise<ApiResponse<MaintenanceRequest>> => {
  try {
    console.log("Assigning request:", requestId, "to user:", userId);
    const payload = { userId };
    console.log("Sending payload:", payload);
    const response = await apiService.put(
      `/maintenance/requests/${requestId}/assign`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Assignment error:", error.response?.data);
    throw (
      error.response?.data || { error: "Failed to assign maintenance request" }
    );
  }
};

export const updateMaintenanceStatus = async (
  requestId: string,
  status: string,
  notes?: string,
  completionCode?: string
): Promise<ApiResponse<MaintenanceRequest>> => {
  try {
    const payload: { status: string; notes?: string; completionCode?: string } = { status };
    if (notes) payload.notes = notes;
    if (completionCode) payload.completionCode = completionCode;
    
    const response = await apiService.put(
      `/maintenance/requests/${requestId}/status`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to update request status" };
  }
};

export const addPublicComment = async (
  requestId: string,
  comment: string
): Promise<ApiResponse<MaintenanceRequest>> => {
  try {
    const response = await apiService.post(
      `/maintenance/requests/${requestId}/comments`,
      { comment }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to add comment" };
  }
};

export const addInternalNote = async (
  requestId: string,
  note: string
): Promise<ApiResponse<MaintenanceRequest>> => {
  try {
    const response = await apiService.post(
      `/maintenance/requests/${requestId}/notes`,
      { note }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to add internal note" };
  }
};

export const addMedia = async (
  requestId: string,
  mediaData: {
    url: string;
    type?: "image" | "video" | "document";
    category?: "before" | "during" | "after" | "issue" | "document";
    description?: string;
  }
): Promise<ApiResponse<MaintenanceRequest>> => {
  try {
    const response = await apiService.post(
      `/maintenance/requests/${requestId}/media`,
      mediaData
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to add media" };
  }
};

export const updateExpenses = async (
  requestId: string,
  expenses: {
    labour?: { amount: number | string; description: string };
    material?: MaterialExpensePayload;
    other?: { amount: number | string; description: string };
  }
): Promise<ApiResponse<any>> => {
  try {
    const response = await apiService.post(
      `/maintenance/requests/${requestId}/expenses`,
      { expenses }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to update expenses" };
  }
};

export const getMaintenanceExpenses = async (
  requestId: string,
  companyId?: string
): Promise<
  ApiResponse<MaintenanceExpenseDocument[]> & {
    totals: { total: number; labour: number; material: number; other: number };
  }
> => {
  try {
    const response = await apiService.get(`/maintenance/requests/${requestId}/expenses`, {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to fetch maintenance expenses" };
  }
};

export const searchMaintenanceMaterialItems = async (
  requestId: string,
  params: { search?: string; limit?: number; companyId?: string }
): Promise<ApiResponse<MaterialSearchItem[]> & { count: number }> => {
  try {
    const response = await apiService.get(
      `/maintenance/requests/${requestId}/material-items/search`,
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to search material items" };
  }
};

export const getMaintenanceStats = async (params?: {
  companyId?: string;
  propertyId?: string;
  rentalUnitId?: string;
  type?: string;
  priority?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}): Promise<
  ApiResponse<{
    totalRequests: number;
    statusCounts: {
      pending: number;
      assigned: number;
      accepted: number;
      inProgress: number;
      onHold: number;
      completed: number;
      closed: number;
      cancelled: number;
    };
    priorityCounts: {
      low: number;
      medium: number;
      high: number;
      urgent: number;
    };
    overdueRequests: number;
    averageResponseTime: number;
    averageCompletionTime: number;
    satisfaction: {
      average: number;
      count: number;
    };
  }>
> => {
  try {
    const response = await apiService.get("/maintenance/requests/stats", {
      params,
    });
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || {
        error: "Failed to fetch maintenance statistics",
      }
    );
  }
};

// Maintenance Type interfaces
export interface MaintenanceType {
  _id: string;
  companyId: string;
  type: string;
  displayName: string;
  description?: string;
  icon?: string;
  color?: string;
  isActive: boolean;
  priority: number;
  estimatedDuration?: {
    value: number;
    unit: "hours" | "days" | "weeks";
  };
  requiresSpecialist?: boolean;
  commonIssues?: Array<{
    title: string;
    description?: string;
  }>;
  customFields?: Array<{
    fieldName: string;
    fieldType: "text" | "number" | "date" | "select" | "checkbox";
    required?: boolean;
    options?: string[];
  }>;
  autoAssignmentEnabled?: boolean;
  notificationSettings?: {
    notifyOnCreate?: boolean;
    notifyOnAssign?: boolean;
    notifyOnComplete?: boolean;
    escalationTime?: {
      value: number;
      unit: "minutes" | "hours" | "days";
    };
    escalateToRole?: "manager" | "owner" | "admin";
  };
  slaSettings?: {
    responseTime?: {
      value: number;
      unit: "minutes" | "hours" | "days";
    };
    resolutionTime?: {
      value: number;
      unit: "hours" | "days" | "weeks";
    };
    priorityMultiplier?: {
      low: number;
      medium: number;
      high: number;
      urgent: number;
    };
  };
  createdBy?: any;
  updatedBy?: any;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateMaintenanceTypeData {
  companyId: string;
  type: string;
  displayName: string;
  description?: string;
  icon?: string;
  color?: string;
  priority?: number;
  estimatedDuration?: {
    value: number;
    unit: "hours" | "days" | "weeks";
  };
  requiresSpecialist?: boolean;
  commonIssues?: Array<{
    title: string;
    description?: string;
  }>;
  customFields?: Array<{
    fieldName: string;
    fieldType: "text" | "number" | "date" | "select" | "checkbox";
    required?: boolean;
    options?: string[];
  }>;
  autoAssignmentEnabled?: boolean;
  notificationSettings?: any;
  slaSettings?: any;
}

// Maintenance Type API functions
export const getMaintenanceTypes = async (
  companyId: string,
  isActive?: boolean
): Promise<ApiResponse<MaintenanceType[]>> => {
  try {
    const params = isActive !== undefined ? { isActive } : {};
    const response = await apiService.get(`/maintenance/types/${companyId}`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to fetch maintenance types" }
    );
  }
};

export const getMaintenanceType = async (
  typeId: string
): Promise<ApiResponse<MaintenanceType>> => {
  try {
    const response = await apiService.get(
      `/maintenance/types/single/${typeId}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to fetch maintenance type" };
  }
};

export const createMaintenanceType = async (
  typeData: CreateMaintenanceTypeData
): Promise<ApiResponse<MaintenanceType>> => {
  try {
    const response = await apiService.post("/maintenance/types", typeData);
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to create maintenance type" }
    );
  }
};

export const updateMaintenanceType = async (
  typeId: string,
  updates: Partial<CreateMaintenanceTypeData>
): Promise<ApiResponse<MaintenanceType>> => {
  try {
    const response = await apiService.put(
      `/maintenance/types/single/${typeId}`,
      updates
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to update maintenance type" }
    );
  }
};

export const deleteMaintenanceType = async (
  typeId: string
): Promise<ApiResponse<void>> => {
  try {
    const response = await apiService.delete(
      `/maintenance/types/single/${typeId}`
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to delete maintenance type" }
    );
  }
};

export const toggleMaintenanceType = async (
  typeId: string
): Promise<ApiResponse<MaintenanceType>> => {
  try {
    const response = await apiService.patch(
      `/maintenance/types/${typeId}/toggle`
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to toggle maintenance type" }
    );
  }
};

export const createDefaultMaintenanceTypes = async (
  companyId: string
): Promise<ApiResponse<MaintenanceType[]>> => {
  try {
    const response = await apiService.post(
      "/maintenance/types/create-defaults",
      { companyId }
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || {
        error: "Failed to create default maintenance types",
      }
    );
  }
};

export const updateSLASettings = async (
  typeId: string,
  slaSettings: any
): Promise<ApiResponse<MaintenanceType>> => {
  try {
    const response = await apiService.patch(
      `/maintenance/types/${typeId}/sla`,
      { slaSettings }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to update SLA settings" };
  }
};

export const addCommonIssue = async (
  typeId: string,
  issue: { title: string; description?: string }
): Promise<ApiResponse<MaintenanceType>> => {
  try {
    const response = await apiService.post(
      `/maintenance/types/${typeId}/common-issues`,
      issue
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to add common issue" };
  }
};

export const removeCommonIssue = async (
  typeId: string,
  issueIndex: number
): Promise<ApiResponse<MaintenanceType>> => {
  try {
    const response = await apiService.delete(
      `/maintenance/types/${typeId}/common-issues/${issueIndex}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to remove common issue" };
  }
};

// Inline assignee and watcher management
export const addMaintenanceTypeAssignee = async (
  typeId: string,
  userId: string
): Promise<ApiResponse<any>> => {
  try {
    const response = await apiService.post(
      `/maintenance/types/${typeId}/assignee`,
      { userId }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to add assignee" };
  }
};

export const removeMaintenanceTypeAssignee = async (
  typeId: string
): Promise<ApiResponse<void>> => {
  try {
    const response = await apiService.delete(
      `/maintenance/types/${typeId}/assignee`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to remove assignee" };
  }
};

export const addMaintenanceTypeWatcher = async (
  typeId: string,
  userId: string
): Promise<ApiResponse<any>> => {
  try {
    const response = await apiService.post(
      `/maintenance/types/${typeId}/watcher`,
      { userId }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to add watcher" };
  }
};

export const removeMaintenanceTypeWatcher = async (
  typeId: string,
  userId: string
): Promise<ApiResponse<void>> => {
  try {
    const response = await apiService.delete(
      `/maintenance/types/${typeId}/watcher/${userId}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to remove watcher" };
  }
};

// Maintenance Assignment interfaces
export interface MaintenanceAssignment {
  _id: string;
  companyId: string;
  maintenanceType: string;
  maintenanceTypeId?: any;
  assignmentMethod: "single" | "round-robin" | "broadcast";
  assignees: Array<{
    userId: any;
    name: string;
    email: string;
    phone?: string;
    isActive: boolean;
    assignedCount: number;
    lastAssigned?: string;
    specializations?: string[];
    availability?: any;
    priority: number;
    maxConcurrentJobs: number;
    currentActiveJobs: number;
  }>;
  watchers: Array<{
    userId: any;
    name: string;
    email: string;
    role?: string;
    notifyOnCreate: boolean;
    notifyOnUpdate: boolean;
    notifyOnComplete: boolean;
    notifyOnEscalation: boolean;
  }>;
  settings: {
    autoAssign: boolean;
    requireAcceptance: boolean;
    acceptanceTimeout: number;
    sendNotifications: boolean;
    allowSelfAssignment?: boolean;
    escalationEnabled?: boolean;
    escalationTime?: number;
    reassignmentAllowed?: boolean;
    maxReassignments?: number;
    roundRobinStrategy?: "sequential" | "load-balanced" | "priority-based";
    broadcastStrategy?: "all" | "available-only" | "priority-groups";
  };
  statistics?: {
    totalAssigned: number;
    totalCompleted: number;
    averageResponseTime?: number;
    averageCompletionTime?: number;
    lastAssignmentDate?: string;
    currentRoundRobinIndex?: number;
  };
  isActive: boolean;
  createdBy?: any;
  updatedBy?: any;
  createdAt?: string;
  updatedAt?: string;
}

// Maintenance Assignment API functions
export const getMaintenanceAssignments = async (
  companyId: string,
  isActive?: boolean
): Promise<ApiResponse<MaintenanceAssignment[]>> => {
  try {
    const params = isActive !== undefined ? { isActive } : {};
    const response = await apiService.get(
      `/maintenance/assignments/${companyId}`,
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || {
        error: "Failed to fetch maintenance assignments",
      }
    );
  }
};

export const getMaintenanceAssignment = async (
  id: string
): Promise<ApiResponse<MaintenanceAssignment>> => {
  try {
    const response = await apiService.get(
      `/maintenance/assignments/single/${id}`
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || {
        error: "Failed to fetch maintenance assignment",
      }
    );
  }
};

export const getAssignmentByType = async (
  companyId: string,
  maintenanceType: string
): Promise<ApiResponse<MaintenanceAssignment>> => {
  try {
    const response = await apiService.get(
      `/maintenance/assignments/${companyId}/type/${maintenanceType}`
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to fetch assignment by type" }
    );
  }
};

export const createOrUpdateAssignment = async (
  data: any
): Promise<ApiResponse<MaintenanceAssignment>> => {
  try {
    const response = await apiService.post("/maintenance/assignments", data);
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to create/update assignment" }
    );
  }
};

export const updateAssignmentSettings = async (
  id: string,
  settings: any
): Promise<ApiResponse<MaintenanceAssignment>> => {
  try {
    const response = await apiService.patch(
      `/maintenance/assignments/${id}/settings`,
      { settings }
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to update assignment settings" }
    );
  }
};

export const addAssignee = async (
  assignmentId: string,
  assigneeData: any
): Promise<ApiResponse<MaintenanceAssignment>> => {
  try {
    const response = await apiService.post(
      `/maintenance/assignments/${assignmentId}/assignees`,
      assigneeData
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to add assignee" };
  }
};

export const removeAssignee = async (
  assignmentId: string,
  userId: string
): Promise<ApiResponse<MaintenanceAssignment>> => {
  try {
    const response = await apiService.delete(
      `/maintenance/assignments/${assignmentId}/assignees/${userId}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to remove assignee" };
  }
};

export const updateAssigneeStatus = async (
  assignmentId: string,
  userId: string,
  isActive: boolean
): Promise<ApiResponse<MaintenanceAssignment>> => {
  try {
    const response = await apiService.patch(
      `/maintenance/assignments/${assignmentId}/assignees/${userId}/status`,
      { isActive }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to update assignee status" };
  }
};

export const addWatcher = async (
  assignmentId: string,
  watcherData: any
): Promise<ApiResponse<MaintenanceAssignment>> => {
  try {
    const response = await apiService.post(
      `/maintenance/assignments/${assignmentId}/watchers`,
      watcherData
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to add watcher" };
  }
};

export const removeWatcher = async (
  assignmentId: string,
  userId: string
): Promise<ApiResponse<MaintenanceAssignment>> => {
  try {
    const response = await apiService.delete(
      `/maintenance/assignments/${assignmentId}/watchers/${userId}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to remove watcher" };
  }
};

export const deleteAssignment = async (
  id: string
): Promise<ApiResponse<void>> => {
  try {
    const response = await apiService.delete(
      `/maintenance/assignments/single/${id}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: "Failed to delete assignment" };
  }
};

export const getAvailableAssignees = async (
  companyId: string
): Promise<ApiResponse<any[]>> => {
  try {
    const response = await apiService.get(
      `/maintenance/assignments/${companyId}/available-users`
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data || { error: "Failed to fetch available assignees" }
    );
  }
};
